import { useCallback } from 'react';
import { showCaseDetail } from '../Services/case.service';
import { markCaseForPatientAutoVerify } from '../../Nczi/Services/patientAutoVerify.service';
import { Backlink } from '../../Base/Enums/Backlink.enum';
import {
	useAssignCaseMutation,
	useReAssignCaseMutation,
	useWorkplaceMemberQuery,
} from '../../../graphql/graphql';
import { useAppContext } from '../../../Services/AppContext';
import useNotification from '../../../Hooks/useNotification.hook';
import useApiClient from '../../Base/Hooks/useApiClient';

export default function useCaseConnect() {
	const { workplaceId, userData } = useAppContext();
	const { displayError } = useApiClient();
	const { showNotificationWarning } = useNotification();

	const [reAssignCase] = useReAssignCaseMutation();
	const [assignCase] = useAssignCaseMutation();

	const { data } = useWorkplaceMemberQuery({
		userId: userData?.id,
		workplaceId,
	});

	const workplaceMemberId = data?.workplaceMember?.id;

	const ensureWorkplaceMemberId = useCallback((): string | null => {
		if (!workplaceMemberId) {
			showNotificationWarning({
				content: 'component.monitoringCenter.errors.missingWorkplaceMember',
			});
			return null;
		}
		return workplaceMemberId;
	}, [showNotificationWarning, workplaceMemberId]);

	const connectCase = async (
		caseId: string,
		mutation: (args: {
			caseId: string;
			workplaceId: string;
			workplaceMemberId: string;
		}) => { unwrap: () => Promise<unknown> },
	) => {
		const memberId = ensureWorkplaceMemberId();
		if (!memberId) return;

		try {
			await mutation({
				caseId,
				workplaceId,
				workplaceMemberId: memberId,
			}).unwrap();

			markCaseForPatientAutoVerify(caseId);

			await showCaseDetail(caseId, Backlink.MONITORING_CENTER);
		} catch (e) {
			displayError(e);
		}
	};

	return {
		handleAssignCase: (caseId: string) => connectCase(caseId, assignCase),
		handleReassignCase: (caseId: string) => connectCase(caseId, reAssignCase),
	};
}
